export const authenticatedFetch = (url, options = {}) => {
  const headers = new Headers(options.headers);
  const token = localStorage.getItem('spike_dashboard_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...options, headers });
};

/**
 * Centralized API Client for Spike Dashboard
 * 
 * Provides a single point of entry for all API calls with:
 * - Consistent error handling
 * - Request/response logging
 * - Automatic retries
 * - Type-safe method signatures
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Custom error class for API errors
 */
class ApiError extends Error {
  constructor(message, status, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Get stored auth token
 */
function getStoredToken() {
  return localStorage.getItem('spike_dashboard_token');
}

/**
 * Core request method with error handling
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get auth token
  const token = options.token || getStoredToken();
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };
  
  // Remove token from options to not include it in the request
  const { token: _, ...restOptions } = options;
  const finalOptions = { ...defaultOptions, ...restOptions };
  
  try {
    const response = await fetch(url, finalOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || `HTTP Error: ${response.status}`,
        response.status,
        errorData.details
      );
    }
    
    return response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    console.error(`API Error: ${endpoint}`, error);
    throw new ApiError(
      error.message || 'Network error',
      0,
      { originalError: error.toString() }
    );
  }
}

/**
 * Spike Dashboard API Client
 */
const apiClient = {
  // =====================
  // Authentication
  // =====================
  
  /**
   * Login with username and password
   * @param {string} username - Username or email
   * @param {string} password - Password
   */
  async login(username, password) {
    return request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  
  /**
   * Register new user
   * @param {string} username - Username
   * @param {string} email - Email address
   * @param {string} password - Password
   */
  async register(username, email, password) {
    return request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
  },
  
  /**
   * Get current user info
   * @param {string} token - Auth token
   */
  async getCurrentUser(token) {
    return request('/api/auth/me', { token });
  },

  /**
   * Get current user's profile and preferences
   */
  async getProfile() {
    return request('/api/auth/profile');
  },

  /** Get the current user's tier limits and measured resource usage. */
  async getQuotaUsage() {
    return request('/api/auth/quota-usage');
  },

  /**
   * Update current user's profile and preferences
   * @param {Object} payload - Profile and preference values
   */
  async updateProfile(payload) {
    return request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /** Browse published, data-free dashboard layouts. */
  async getLayoutGallery() {
    return request('/api/layout-gallery');
  },

  async getSharedLayoutTemplate(token) {
    return request(`/api/layout-gallery/shared/${encodeURIComponent(token)}`);
  },

  /** Publish a dashboard layout to the gallery. */
  async publishLayoutTemplate(payload) {
    return request('/api/layout-gallery', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** Remove a layout template owned by the current user. */
  async deleteLayoutTemplate(templateId) {
    return request(`/api/layout-gallery/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },
  
  /**
   * Logout current user
   * @param {string} token - Auth token
   */
  async logout(token) {
    return request('/api/auth/logout', {
      method: 'POST',
      token,
    });
  },

  /** Update the current account's online heartbeat. */
  async updatePresence() {
    return request('/api/auth/presence', { method: 'POST' });
  },
  
  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  async changePassword(currentPassword, newPassword) {
    return request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },
  
  /**
   * List all users (admin only)
   */
  async listUsers() {
    return request('/api/auth/users');
  },

  /** Load user presence and tier quotas (admin only). */
  async getAssistantUsage(params) {
    return request(`/api/auth/admin/assistant-usage?${new URLSearchParams(params)}`);
  },

  async getAdminOverview() {
    return request('/api/auth/admin/overview');
  },

  /** Update resource limits for an account tier (admin only). */
  async updateTierQuota(tier, quota) {
    return request(`/api/auth/admin/quotas/${encodeURIComponent(tier)}`, {
      method: 'PUT',
      body: JSON.stringify(quota),
    });
  },

  /**
   * Create a user (admin only)
   * @param {Object} payload - New user data
   */
  async createUser(payload) {
    return request('/api/auth/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  
  /**
   * Update user role (admin only)
   * @param {number} userId - User ID
   * @param {string} role - New role ('guest', 'user', 'pro', or 'admin')
   */
  async updateWidgetPermission(userId, widgetId, allowed) {
    return request(`/api/auth/users/${userId}/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify({ allowed }),
    });
  },

  async updateUserRole(userId, role) {
    return request(`/api/auth/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },
  
  /**
   * Delete user (admin only)
   * @param {number} userId - User ID
   */
  async deleteUser(userId) {
    return request(`/api/auth/users/${userId}`, {
      method: 'DELETE',
    });
  },
  
  // =====================
  // Health & Info
  // =====================
  
  /**
   * Check API health status
   */
  async healthCheck() {
    return request('/health');
  },
  
  /**
   * Get current dataset information
   */
  async getDatasetInfo() {
    return request('/api/dataset-info');
  },
  
  // =====================
  // Dataset Management
  // =====================
  
  /**
   * List all available datasets
   */
  async getDatasets() {
    return request('/api/datasets');
  },
  
  /**
   * Set the current active dataset
   * @param {string} datasetName - Name of the dataset to load
   */
  async setDataset(datasetName, recordingFormat) {
    return request('/api/dataset/set', {
      method: 'POST',
      body: JSON.stringify({ dataset: datasetName, recordingFormat }),
    });
  },
  
  /**
   * Delete a dataset
   * @param {string} datasetName - Name of the dataset to delete
   */
  async setDatasetVisibility(datasetName, isPublic) {
    return request('/api/dataset/visibility', {
      method: 'PATCH', body: JSON.stringify({ dataset: datasetName, isPublic }),
    });
  },

  async deleteDataset(datasetName) {
    return request('/api/dataset/delete', {
      method: 'DELETE',
      body: JSON.stringify({ dataset: datasetName }),
    });
  },
  
  /**
   * Upload a new dataset (uses FormData for file upload)
   * @param {File} file - The dataset file
   * @param {File} spikeTimesFile - Optional spike times file
   * @param {Function} onProgress - Optional progress callback
   */
  async uploadDataset(file, spikeTimesFile = null, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (spikeTimesFile) {
      formData.append('spike_times_file', spikeTimesFile);
    }
    
    const url = `${API_BASE_URL}/api/dataset/upload`;
    
    // Note: We can't use the standard request function here due to FormData
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getStoredToken()}` },
      body: formData,
      // Don't set Content-Type - browser will set it with boundary
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || `Upload failed: ${response.status}`,
        response.status
      );
    }
    
    return response.json();
  },
  
  // =====================
  // Label Mappings
  // =====================
  
  /**
   * Get all dataset-to-label mappings
   */
  async getLabelMappings() {
    return request('/api/label-mappings');
  },
  
  /**
   * Add or update a label mapping
   * @param {string} datasetName - Dataset name
   * @param {string} labelName - Label file name
   */
  async addLabelMapping(datasetName, labelName) {
    return request('/api/label-mappings', {
      method: 'POST',
      body: JSON.stringify({ dataset: datasetName, label: labelName }),
    });
  },
  
  /**
   * Delete a label mapping
   * @param {string} datasetName - Dataset name to remove mapping for
   */
  async deleteLabelMapping(datasetName) {
    return request(`/api/label-mappings/${encodeURIComponent(datasetName)}`, {
      method: 'DELETE',
    });
  },
  
  // =====================
  // Spike Data
  // =====================
  
  /**
   * Fetch spike data for specified channels
   * @param {Object} params - Request parameters
   */
  async getSpikeData({
    channels,
    startTime,
    endTime,
    spikeThreshold = null,
    invertData = false,
    usePrecomputed = false,
    dataType = 'raw',
    filterType = 'highpass',
  }) {
    return request('/api/spike-data', {
      method: 'POST',
      body: JSON.stringify({
        channels,
        startTime,
        endTime,
        spikeThreshold,
        invertData,
        usePrecomputed,
        dataType,
        filterType,
      }),
    });
  },

  /** Get a bounded channel-by-time trace image. */
  async getTraceHeatmap({
    startSample,
    endSample,
    channelIds = null,
    maxTimeBins = 1000,
    maxChannels = 512,
    normalization = 'robust_zscore',
  }) {
    return request('/api/trace-heatmap', {
      method: 'POST',
      body: JSON.stringify({
        startSample,
        endSample,
        channelIds,
        maxTimeBins,
        maxChannels,
        normalization,
      }),
    });
  },
  
  /**
   * Check if precomputed spike times are available
   */
  async checkSpikeTimesAvailable() {
    return request('/api/spike-times-available');
  },
  
  /**
   * Navigate to next/previous spike
   * @param {number} currentTime - Current time position
   * @param {string} direction - 'next' or 'prev'
   * @param {number[]} channels - Channels to search
   */
  async navigateSpike(currentTime, direction, channels) {
    return request('/api/navigate-spike', {
      method: 'POST',
      body: JSON.stringify({ currentTime, direction, channels }),
    });
  },
  
  /**
   * Get spike waveform preview
   * @param {Object} params - Request parameters
   */
  async getSpikePreview({ spikeTime, channelId, window = 10, filterType = 'highpass', pointIndex = 0 }) {
    return request('/api/spike-preview', {
      method: 'POST',
      body: JSON.stringify({ spikeTime, channelId, window, filterType, pointIndex }),
    });
  },
  
  // =====================
  // Clustering
  // =====================
  
  /**
   * Get cluster data for visualization
   * @param {string} mode - 'synthetic', 'real', or 'algorithm_results'
   * @param {Object} channelMapping - Channel mapping for clusters
   */
  async getClusterData(mode = 'synthetic', channelMapping = {}) {
    return request('/api/cluster-data', {
      method: 'POST',
      body: JSON.stringify({ mode, channelMapping }),
    });
  },
  
  /**
   * Get cluster statistics
   * @param {number[]} clusterIds - IDs of clusters to get stats for
   * @param {string} algorithm - Algorithm used for clustering
   */
  async getClusterStatistics(clusterIds, algorithm = '') {
    return request('/api/cluster-statistics', {
      method: 'POST',
      body: JSON.stringify({ clusterIds, algorithm }),
    });
  },

  /** Get physical or fallback probe geometry and cluster footprints. */
  async getProbeGeometry({ clusterIds = [], algorithm = '' }) {
    return request('/api/probe-geometry', {
      method: 'POST',
      body: JSON.stringify({ clusterIds, algorithm }),
    });
  },

  /**
   * Get an auto/cross-correlogram matrix for selected clusters.
   */
  async getClusterCorrelograms({
    clusterIds,
    algorithm = '',
    binSizeMs = 1,
    windowSizeMs = 50,
    maxSpikesPerCluster = 100000,
  }) {
    return request('/api/cluster-correlograms', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        binSizeMs,
        windowSizeMs,
        maxSpikesPerCluster,
      }),
    });
  },

  /**
   * Get per-cluster inter-spike interval histograms.
   */
  async getClusterIsiHistograms({
    clusterIds,
    algorithm = '',
    binSizeMs = 0.5,
    windowSizeMs = 100,
    refractoryPeriodMs = 2,
  }) {
    return request('/api/cluster-isi-histograms', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        binSizeMs,
        windowSizeMs,
        refractoryPeriodMs,
      }),
    });
  },

  /**
   * Rank clusters similar to a primary cluster for pair review.
   */
  async getClusterSimilarities({
    primaryClusterId,
    candidateClusterIds = null,
    algorithm = '',
    maxCandidates = 20,
    maxSpikesPerCluster = 100,
    windowSamples = 15,
  }) {
    return request('/api/cluster-similarities', {
      method: 'POST',
      body: JSON.stringify({
        primaryClusterId,
        candidateClusterIds,
        algorithm,
        maxCandidates,
        maxSpikesPerCluster,
        windowSamples,
      }),
    });
  },

  /**
   * Get binned spike counts and firing rates through recording time.
   */
  async getClusterFiringRates({
    clusterIds,
    algorithm = '',
    binSizeSeconds = 1,
    maxBins = 5000,
  }) {
    return request('/api/cluster-firing-rates', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        binSizeSeconds,
        maxBins,
      }),
    });
  },

  /**
   * Get unstandardized peak-to-peak amplitudes through recording time.
   */
  async getClusterAmplitudeProfiles(clusterIds, algorithm = '', clusters, options = {}) {
    return request('/api/cluster-amplitude-profiles', {
      method: 'POST', signal: options.signal, body: JSON.stringify({ clusterIds, algorithm, clusters, recordingName: options.recordingName }),
    });
  },

  async getClusterAmplitudes({
    clusterIds,
    algorithm = '',
    maxSpikesPerCluster = 5000,
    windowSamples = 15,
    includeBackground = true,
    maxBackgroundSpikes = 5000,
  }) {
    return request('/api/cluster-amplitudes', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        maxSpikesPerCluster,
        windowSamples,
        includeBackground,
        maxBackgroundSpikes,
      }),
    });
  },

  /**
   * Discover and load one typed per-spike attribute.
   */
  async getSpikeAttributes({
    clusterIds,
    algorithm = '',
    attributeId = '',
    maxSpikesPerCluster = 5000,
  }) {
    return request('/api/spike-attributes', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        attributeId,
        maxSpikesPerCluster,
      }),
    });
  },

  /**
   * Get ordered retained templates or deterministic raw mean waveforms.
   */
  async getClusterTemplates({
    clusterIds,
    algorithm = '',
    windowSamples = 30,
    maxWaveforms = 64,
  }) {
    return request('/api/cluster-templates', {
      method: 'POST',
      body: JSON.stringify({ clusterIds, algorithm, windowSamples, maxWaveforms }),
    });
  },

  /**
   * Get bounded per-spike features for matrix and pair-review views.
   */
  async getClusterFeatures({
    clusterIds,
    algorithm = '',
    maxSpikesPerCluster = 5000,
    includeBackground = true,
    maxBackgroundSpikes = 5000,
    selectedChannels = [],
  }) {
    return request('/api/cluster-features', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        algorithm,
        maxSpikesPerCluster,
        includeBackground,
        maxBackgroundSpikes,
        selectedChannels,
      }),
    });
  },
  
  /**
   * Get cluster waveforms
   * @param {Object} params - Request parameters
   */
  async getSpikeWaveformCards({ spikes, neighborRadius = 2, windowSize = 30, signal }) {
    return request('/api/spike-waveform-cards', {
      method: 'POST', signal,
      body: JSON.stringify({ spikes, neighborRadius, windowSize }),
    });
  },

  async getClusterWaveforms({
    clusterIds,
    clusters = [],
    maxWaveforms = 100,
    windowSize = 30,
    algorithm = '',
    includeSpikeIndices = []
  }) {
    return request('/api/cluster-waveforms', {
      method: 'POST',
      body: JSON.stringify({
        clusterIds,
        clusters,
        maxWaveforms,
        windowSize,
        algorithm,
        includeSpikeIndices
      }),
    });
  },
  
  /**
   * Get multi-channel waveforms for a cluster
   * @param {Object} params - Request parameters
   */
  async getClusterMultiChannelWaveforms({ clusterId, maxWaveforms = 50, windowSize = 30, algorithm = '' }) {
    return request('/api/cluster-multi-channel-waveforms', {
      method: 'POST',
      body: JSON.stringify({ clusterId, maxWaveforms, windowSize, algorithm }),
    });
  },

  /**
   * Parse a cluster comparison file (.json or .mat).
   * @param {File} file - Cluster comparison file
   */
  async listCuratorFiles() {
    return request('/api/auth/curator-files');
  },

  async loadCuratorFile(id) {
    return request(`/api/auth/curator-files/${encodeURIComponent(id)}`);
  },

  async deleteCuratorFile(id) {
    return request(`/api/auth/curator-files/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  async renameCuratorFile(id, name) {
    return request(`/api/auth/curator-files/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify({ name }),
    });
  },

  async parseClusterComparisonFile(file, { save = false } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('save', String(save));

    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/cluster-comparison/parse-file`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getStoredToken()}` },
      body: formData,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || `Upload failed: ${response.status}`,
        response.status,
        errorData.details
      );
    }

    return response.json();
  },

  /**
   * Predict missing primary channels for cluster data using the loaded dataset.
   * @param {Array} clusters - Normalized cluster rows with spikeTimes
   * @param {number} sampleLimit - Max spike samples per cluster
   */
  async predictPrimaryChannels(clusters, sampleLimit = 5000) {
    return request('/api/cluster-comparison/predict-primary-channels', {
      method: 'POST',
      body: JSON.stringify({ clusters, sampleLimit }),
    });
  },
  
  // =====================
  // Spike Sorting
  // =====================
  
  /**
   * List available spike sorting algorithms
   */
  async getAlgorithms() {
    return request('/api/spike-sorting/algorithms');
  },

  /**
   * List linked custom spike sorting pipelines.
   */
  async getCustomPipelines() {
    return request('/api/spike-sorting/custom-pipelines');
  },

  /**
   * Register a linked custom spike sorting pipeline.
   * @param {Object} pipeline - Pipeline repository metadata
   */
  async addCustomPipeline(pipeline) {
    return request('/api/spike-sorting/custom-pipelines', {
      method: 'POST',
      body: JSON.stringify(pipeline),
    });
  },

  /**
   * Delete a linked custom spike sorting pipeline.
   * @param {string} pipelineId - Pipeline id
   */
  async deleteCustomPipeline(pipelineId) {
    return request(`/api/spike-sorting/custom-pipelines/${encodeURIComponent(pipelineId)}`, {
      method: 'DELETE',
    });
  },
  
  /**
   * Run spike sorting algorithm
   * @param {string} algorithm - Algorithm name
   * @param {Object} parameters - Algorithm parameters
   */
  async runSpikeSorting(algorithm, parameters = {}) {
    return request('/api/spike-sorting/run', {
      method: 'POST',
      body: JSON.stringify({ algorithm, parameters }),
    });
  },

  /**
   * Start a spike sorting pipeline job.
   * @param {string} algorithm - Algorithm name
   * @param {Object} parameters - Algorithm parameters
   */
  async startSpikeSortingPipeline(algorithm, parameters = {}) {
    return request('/api/spike-sorting/pipeline/run', {
      method: 'POST',
      body: JSON.stringify({ algorithm, parameters }),
    });
  },

  /**
   * Get current spike sorting pipeline job status.
   */
  async getSpikeSortingPipelineStatus() {
    return request('/api/spike-sorting/pipeline/status');
  },

  /**
   * Request stop for the active spike sorting pipeline job.
   */
  async stopSpikeSortingPipeline() {
    return request('/api/spike-sorting/pipeline/stop', {
      method: 'POST',
    });
  },
  
  /**
   * Get stored clustering results
   */
  async getClusteringResults() {
    return request('/api/clustering-results');
  },
};

// Export for ES modules
export { apiClient as default, ApiError, API_BASE_URL };
